// LAP-733 (GE022) — UI language selector for the Settings page.
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
} from '@mui/material'
import i18n, { SUPPORTED_LANGUAGES, LANGUAGE_META } from '../i18n'
import { makePatchRequest } from '../utils/api'

const LanguageSelector = ({ value, onChange, disablePersist = false, compact = false }) => {
  const { t } = useTranslation('layout')
  const [current, setCurrent] = useState(
    SUPPORTED_LANGUAGES.includes(value) ? value : i18n.language?.slice(0, 2) || 'fr'
  )
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })

  const handleChange = async (event) => {
    const next = event.target.value
    if (!SUPPORTED_LANGUAGES.includes(next)) return

    const previous = current
    setCurrent(next)

    // Immediate UI switch (no page reload).
    try {
      await i18n.changeLanguage(next)
    } catch (_) {
      // Fall through; we still try to persist.
    }

    if (typeof onChange === 'function') {
      onChange(next)
    }

    // LAP-752 — when the picker is used on the auth pages (disablePersist=true)
    // we can't PATCH the backend (no auth token), but we *do* want the
    // explicit choice to win once the user logs in. Drop a marker that
    // NavBar.fetchUser reads and forwards to the backend on first auth.
    if (disablePersist) {
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('i18nExplicitChoice', next)
        }
      } catch (_) {
        /* ignore quota / disabled storage errors */
      }
      return
    }

    // Persist to backend.
    try {
      const response = await makePatchRequest(
        '/api/user/preferences/',
        { language: next },
        {},
        true
      )
      if (response && response.status >= 200 && response.status < 300) {
        setSnack({ open: true, severity: 'success', msg: t('languageSelector.saveSuccess') })
      } else {
        // Roll back on failure.
        setCurrent(previous)
        await i18n.changeLanguage(previous)
        setSnack({ open: true, severity: 'error', msg: t('languageSelector.saveError') })
      }
    } catch (_e) {
      setCurrent(previous)
      try {
        await i18n.changeLanguage(previous)
      } catch (_) {}
      setSnack({ open: true, severity: 'error', msg: t('languageSelector.saveError') })
    }
  }

  // LAP-733 (GE022) — compact mode hides the label + helper text and
  // shrinks the control so the picker fits unobtrusively in the auth header.
  if (compact) {
    return (
      <Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            id="language-selector-compact"
            value={current}
            onChange={handleChange}
            inputProps={{ 'data-testid': 'language-selector', 'aria-label': t('languageSelector.label') }}
            sx={{
              '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 },
            }}
          >
            {SUPPORTED_LANGUAGES.map((lng) => {
              const meta = LANGUAGE_META[lng]
              return (
                <MenuItem key={lng} value={lng} data-testid={`language-option-${lng}`}>
                  <span style={{ marginInlineEnd: 8 }}>{meta.flag}</span>
                  {meta.native}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    )
  }

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel id="language-selector-label">{t('languageSelector.label')}</InputLabel>
        <Select
          labelId="language-selector-label"
          id="language-selector"
          value={current}
          label={t('languageSelector.label')}
          onChange={handleChange}
          inputProps={{ 'data-testid': 'language-selector' }}
        >
          {SUPPORTED_LANGUAGES.map((lng) => {
            const meta = LANGUAGE_META[lng]
            return (
              <MenuItem key={lng} value={lng} data-testid={`language-option-${lng}`}>
                <span style={{ marginInlineEnd: 8 }}>{meta.flag}</span>
                {meta.native}
              </MenuItem>
            )
          })}
        </Select>
        <FormHelperText>{t('languageSelector.helper')}</FormHelperText>
      </FormControl>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default LanguageSelector
